import {
  DrumParentCategories,
  KeyAndScale,
  KeyDisplayIndex,
  MelodicParentCategories
} from '@arpeggi/shared'
import * as ModalPrimitive from '@radix-ui/react-dialog'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react'
import * as Dropzone from 'react-dropzone'
import { Updater } from 'use-immer'
import { Genre } from '../../../../client/Genres'
import { useAuth } from '../../../../hooks/useAuth'
import { styled } from '../../../../lib/stitches'
import { SoundType } from '../../../../types/sounds'
import Button from '../../../core/Button/Button'
import { Column, IconButton, Row } from '../../../core/Components'
import Dropdown, { DropdownOption } from '../../../core/Dropdown'
import { FieldErrorLabel } from '../../../core/FieldErrorLabel'
import { FilePicker } from '../../../core/FilePicker'
import {
  EditIcon,
  FileCheckIcon,
  FileCrossIcon,
  PauseIcon,
  PlayIcon,
  RepeatIcon,
  TrashIcon
} from '../../../core/icons'
import Input from '../../../core/Input'
import { RadioOptions } from '../../../core/RadioSelect'
import { Subtitle } from '../../../core/Subtitle'
import { Title } from '../../../core/Title'
import { InternalEndpoint } from '../../../Internal'
import { gray12, gray3, gray9 } from '../../../platform/color'
import {
  ColumnLabel,
  FooterContainer,
  HeaderContainer,
  InstrumentType,
  SectionHeader,
  SoundValidation
} from './MintSoundModalContent'

export const segmentButtonClick = (button: string, location: string) => {
  window.analytics.track('Button Clicked', {
    button: button,
    location: location,
    source: window.location.pathname
  })
}

const melodicSelectOptions: DropdownOption<InstrumentType>[] = Object.keys(
  MelodicParentCategories
).flatMap((parentCategory) => {
  return MelodicParentCategories[parentCategory].map((category) => {
    return { value: category, label: parentCategory + ' - ' + category }
  })
})

const drumSelectOptions: DropdownOption<InstrumentType>[] = Object.keys(
  DrumParentCategories.OneShot
).flatMap((parentCategory) => {
  if (DrumParentCategories.OneShot[parentCategory].length === 0) {
    return { value: parentCategory, label: parentCategory }
  }
  return DrumParentCategories.OneShot[parentCategory].map((category) => {
    return { value: category, label: parentCategory + ' - ' + category }
  })
})

const instrumentSelectOptions = melodicSelectOptions
  .concat(drumSelectOptions)
  .sort((a, b) => a.label.localeCompare(b.label))

const oneShotKeySelectOptions = () => {
  let options: DropdownOption<KeyDisplayIndex>[] = []
  for (let i = 0; i < 13; i++) {
    const keyAndScale = KeyAndScale.from(i)
    options.push({ value: i, label: keyAndScale.displayName() })
  }
  return options
}

export const loopStemKeySelectOptions = () => {
  let options: DropdownOption<KeyDisplayIndex>[] = []

  // Add "None"
  options.push({ value: 0, label: KeyAndScale.from(0).displayName() })

  // Add only Major and Minor scales
  for (let i = 13; i < 37; i++) {
    options.push({ value: i, label: KeyAndScale.from(i).displayName() })
  }
  return options
}

// Behind a feature-flag.
const oneShotSoundTypeRadioSelectOptions: RadioOptions<SoundType>[] = [
  { value: 'one-shot', label: 'One-shot' }
]
const allSoundTypeRadioSelectOptions: RadioOptions<SoundType>[] = [
  { value: 'one-shot', label: 'One-shot' },
  { value: 'stem', label: 'Clip' }
]
const internalSoundTypeRadioSelectOptions: RadioOptions<SoundType>[] = [
  { value: 'one-shot', label: 'One-shot' },
  { value: 'stem', label: 'Clip' },
  { value: 'loop', label: 'Loop' }
]

export type UploadSoundModalForm = {
  instrument: string | null
  genres: Genre[]
  sampleTitle: string
  soundType: SoundType
  key: number
  bpm: number
  duration: number
  file: File
}

export const RowContainer = styled(Row, {
  height: '40px',
  borderWidth: '1px',
  borderColor: 'rgb(235,235,235)',
  borderStyle: 'solid',
  borderTop: 'none'
})

interface UploadedSoundsTableRowProps {
  soundValidation: SoundValidation
  updateInstrument: (newValue: any) => void
  updateSoundType: (newValue: any) => void
  updateSampleTitle: (newValue: any) => void
  updateKey: (newValue: any) => void
  updateBpm: (newValue: any) => void
  deleteRow: () => void
}
const UploadedSoundsTableRow: FC<UploadedSoundsTableRowProps> = ({
  soundValidation,
  updateInstrument,
  updateSoundType,
  updateSampleTitle,
  updateKey,
  updateBpm,
  deleteRow
}) => {
  const [isHover, setIsHover] = useState<boolean>(false)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const audio = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    return () => {
      if (audio.current) audio.current.pause()
    }
  }, [])

  const playOrPause = () => {
    if (!soundValidation.accepted) return
    if (isPlaying) {
      audio.current.pause()
      setIsPlaying(false)
      return
    }
    setIsPlaying(true)
    const dataURL = URL.createObjectURL(soundValidation.form.file)
    audio.current = new Audio(dataURL)

    audio.current.onloadedmetadata = async () => {
      audio.current.onended = () => setIsPlaying(false)
      audio.current.play()
    }
  }

  const iconMargin = '8px'
  const successFileIcon = (
    <FileCheckIcon
      height="20px"
      width="20px"
      style={{ color: '#5EC271', marginRight: iconMargin }}
    />
  )
  const failureFileIcon = (
    <FileCrossIcon
      height="20px"
      width="20px"
      style={{ color: '#EE867F', marginRight: iconMargin }}
    />
  )
  const playIcon = (
    <PlayIcon
      height="20px"
      width="20px"
      style={{ color: gray9, alignSelf: 'center', marginRight: iconMargin }}
    />
  )
  const pauseIcon = (
    <PauseIcon
      height="20px"
      width="20px"
      style={{ color: gray9, alignSelf: 'center', marginRight: iconMargin }}
    />
  )
  const successIcon = (
    <EditIcon
      height="20px"
      width="20px"
      style={{ color: gray9, alignSelf: 'center', marginLeft: iconMargin }}
    />
  )
  const failureIcon = (
    <RepeatIcon
      height="20px"
      width="20px"
      style={{ color: gray9, alignSelf: 'center', marginLeft: iconMargin }}
    />
  )
  const flags = useFlags()

  const visibleError: string | undefined = Object.values(soundValidation.errors).filter(
    (error) => !!error
  )[0]

  const urlIsInternal = window.location.href.includes(`/${InternalEndpoint}`)
  return (
    <Column onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
      <Row css={{ fontSize: '12px' }}>
        <RowContainer css={{ flex: '2' }} verticalCenter>
          <Row css={{ width: '100%', padding: '0px 12px' }} verticalCenter>
            <Row onClick={playOrPause}>
              {soundValidation.errors.file
                ? failureFileIcon
                : isPlaying
                ? pauseIcon
                : isHover
                ? playIcon
                : successFileIcon}
            </Row>
            <Input
              onChange={(e) => updateSampleTitle(e.target.value)}
              value={soundValidation.form.sampleTitle}
              css={{ width: '100%' }}
              disabled={!soundValidation.accepted}
              clear
            />
            {soundValidation.errors.file ? failureIcon : successIcon}
          </Row>
        </RowContainer>
        <RowContainer css={{ flex: '1' }} justifySpaceBetween>
          <Dropdown
            placeholder="Key"
            options={
              soundValidation.form.soundType === 'one-shot'
                ? oneShotKeySelectOptions()
                : loopStemKeySelectOptions()
            }
            value={soundValidation.form.key}
            disabled={!soundValidation.accepted}
            onSelect={(val) => updateKey(val.value)}
            labelCSS={{ fontSize: '12px', color: gray12, width: '100%' }}
          />
        </RowContainer>
        <RowContainer css={{ flex: '1' }} justifySpaceBetween>
          <Input
            onChange={(e) => updateBpm(e.target.value)}
            value={soundValidation.form.bpm || ''}
            css={{ width: '100%', padding: '0px 12px' }}
            disabled={soundValidation.form.soundType === 'one-shot' || !soundValidation.accepted}
            clear
          />
        </RowContainer>
        <RowContainer css={{ flex: '1.5' }} justifySpaceBetween>
          <Dropdown
            placeholder="Instrument"
            options={instrumentSelectOptions}
            value={soundValidation.form.instrument}
            disabled={!soundValidation.accepted}
            onSelect={(val) => updateInstrument(val.value)}
            labelCSS={{ fontSize: '12px', color: gray12, width: '100%' }}
          />
        </RowContainer>
        <RowContainer css={{ flex: '1.5' }} justifySpaceBetween>
          <Dropdown
            placeholder="Sound type"
            options={
              urlIsInternal
                ? internalSoundTypeRadioSelectOptions
                : flags.stemAndLoopUpload
                ? allSoundTypeRadioSelectOptions
                : oneShotSoundTypeRadioSelectOptions
            }
            value={soundValidation.form.soundType}
            disabled={!soundValidation.accepted}
            onSelect={(val) => updateSoundType(val.value)}
            labelCSS={{ fontSize: '12px', color: gray12, width: '100%' }}
          />
        </RowContainer>
        <RowContainer
          css={{ width: '40px', paddingLeft: '7px', paddingRight: '7px' }}
          horizontalCenter
          verticalCenter
        >
          <IconButton onClick={() => deleteRow()}>
            <TrashIcon />
          </IconButton>
        </RowContainer>
      </Row>
      {visibleError && <FieldErrorLabel>{visibleError}</FieldErrorLabel>}
    </Column>
  )
}

const UploadedSoundsTableHeader = () => (
  <Row css={{ backgroundColor: gray3, borderRadius: '8px 8px 0px 0px' }}>
    <Row css={{ flex: '2' }} verticalCenter>
      <ColumnLabel>Title*</ColumnLabel>
    </Row>
    <Row css={{ flex: '1' }} verticalCenter>
      <ColumnLabel>Key*</ColumnLabel>
    </Row>
    <Row css={{ flex: '1' }} verticalCenter>
      <ColumnLabel>BPM</ColumnLabel>
    </Row>
    <Row css={{ flex: '1.5' }} verticalCenter>
      <ColumnLabel>Instrument*</ColumnLabel>
    </Row>
    <Row css={{ flex: '1.5' }} verticalCenter>
      <ColumnLabel>Sound type*</ColumnLabel>
    </Row>
    <Row css={{ width: '40px' }} verticalCenter />
  </Row>
)

interface UploadedSoundsTableProps {
  soundValidations: SoundValidation[]
  setSoundValidations: Updater<SoundValidation[]>
}

const UploadedSoundsTable: FC<UploadedSoundsTableProps> = ({
  soundValidations,
  setSoundValidations
}) => {
  const updateInstrument = (index: number) => (newValue: string) => {
    setSoundValidations((draft) => {
      draft[index].form.instrument = newValue
    })
  }

  const updateSoundType = (index: number) => (newValue: SoundType) => {
    setSoundValidations((draft) => {
      draft[index].form.soundType = newValue
      // Important to reset these values.
      if (newValue === 'one-shot') {
        draft[index].form.bpm = 0
        draft[index].form.key = 0
      }
    })
  }

  const updateSampleTitle = (index: number) => (newValue: string) => {
    setSoundValidations((draft) => {
      draft[index].form.sampleTitle = newValue
    })
  }

  const updateKey = (index: number) => (newValue: number) => {
    setSoundValidations((draft) => {
      draft[index].form.key = newValue
    })
  }

  const updateBpm = (index: number) => (newValue: number) => {
    setSoundValidations((draft) => {
      draft[index].form.bpm = newValue
    })
  }

  const deleteRow = (index: number) => () => {
    setSoundValidations((draft) => {
      draft.splice(index, 1)
    })
  }

  return (
    <Column css={{ paddingBottom: '32px' }}>
      <UploadedSoundsTableHeader />
      {soundValidations.map(
        (soundValidation, index) =>
          soundValidation && (
            <UploadedSoundsTableRow
              soundValidation={soundValidation}
              key={soundValidation.name}
              updateInstrument={updateInstrument(index)}
              updateSoundType={updateSoundType(index)}
              updateSampleTitle={updateSampleTitle(index)}
              updateKey={updateKey(index)}
              updateBpm={updateBpm(index)}
              deleteRow={deleteRow(index)}
            />
          )
      )}
    </Column>
  )
}

interface Props {
  soundValidations: SoundValidation[]
  handleSubmit: (customArtistAddress?: string, customTags?: string[]) => void
  onCancel: () => void
  getInputProps: <T extends Dropzone.DropzoneInputProps>(props?: T) => T
  getRootProps: <T extends Dropzone.DropzoneRootProps>(props?: T) => T
  setSoundValidations: Updater<SoundValidation[]>
  isValid: () => boolean
}

const MintSoundUploadPage: FC<PropsWithChildren<Props>> = ({
  soundValidations,
  setSoundValidations,
  handleSubmit,
  onCancel,
  getInputProps,
  getRootProps,
  isValid,
  children
}) => {
  const flags = useFlags()
  const [customArtistAddress, setCustomArtistAddress] = useState<string>(null)
  const [customTag1, setCustomTag1] = useState<string>(null)
  const [customTag2, setCustomTag2] = useState<string>(null)
  const [customTag3, setCustomTag3] = useState<string>(null)
  const { user } = useAuth()
  return (
    <Column css={{ width: '745px' }}>
      <HeaderContainer>
        <Title>{'Upload & Mint Your Sounds'}</Title>
        <Subtitle>
          {flags.stemAndLoopUpload
            ? `Samples must be an original work and submitted in .wav format. One-shot samples (0-10 ` +
              `seconds) and stems (3 seconds - 5 minutes) are supported. Please label your ` +
              `key and instrument accurately.`
            : `Samples must be an original work and submitted in .wav format. Currently, one-shot samples (0-10 ` +
              `seconds) are supported. Please label your ` +
              `key and instrument accurately.`}
        </Subtitle>
      </HeaderContainer>
      {flags.enableCustomArtistAddress && user?.type === 'admin' && (
        <Column css={{ paddingTop: '24px' }}>
          <Input
            id="artist"
            placeholder="Custom Artist Address"
            value={customArtistAddress}
            onChange={(value) => setCustomArtistAddress(value.currentTarget.value)}
          />
        </Column>
      )}
      <Column css={{ paddingTop: '24px' }}>
        <SectionHeader>Upload sounds</SectionHeader>
        <FilePicker getInputProps={getInputProps} getRootProps={getRootProps} />
        {soundValidations.length > 0 && (
          <>
            <SectionHeader>Your sounds</SectionHeader>
            <UploadedSoundsTable
              soundValidations={soundValidations}
              setSoundValidations={setSoundValidations}
            />
          </>
        )}
        {children}
        <FooterContainer css={{ justifyContent: 'end' }}>
          <ModalPrimitive.Close asChild>
            <Button
              type={'outlined'}
              big={true}
              onClick={onCancel}
              css={{ marginRight: '12px' }}
              modalButton
            >
              Cancel
            </Button>
          </ModalPrimitive.Close>

          <Button
            type={'dark'}
            big={true}
            disabled={
              !isValid() ||
              soundValidations.length === 0 ||
              !!soundValidations.find((item) => !item.accepted)
            }
            onClick={() => {
              const tags = []
              if (customTag1) tags.push(customTag1)
              if (customTag2) tags.push(customTag2)
              if (customTag3) tags.push(customTag3)
              return handleSubmit(customArtistAddress, tags)
            }}
            modalButton
          >
            Mint Sounds
          </Button>
        </FooterContainer>
      </Column>
    </Column>
  )
}

export default MintSoundUploadPage
